import { useState, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import LoadingSvg from "../loadingSvg/LoadingSvg";
import Icon from "../icon/Icon";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";

import { GlobalContext } from "../contextProvider/ContextProvider";
import {
    getFullGradeName,
    validTeacherGradeName,
    getTeacherGradeNamePrefixFromInstructionalArea,
    formatPercentageViewed,
    filterTeachersWithRosterOrEpr
} from "../../utils/teacherOverview/LocationTeacherOverviewUtils";
import { sortGrades } from "../../utils/SchoolAdmin/translations";
import { buildOverviewSyParam } from "./overviewSchoolYear";

import "../../styles/StudentRosterTable.scss";
import * as locationTeacherOverviewStyles from "../../styles/LocationTeacherOverview.module.scss";

/**
 * This component displays the teacher overview content for a specific location and selected semester.
 * It is designed to be used within the TeacherOverviewHome component, either directly for principals
 * or within an Accordion for EPR Admins.
 * @name LocationTeacherOverview
 * @param {Object} props
 * @param {Object} props.locationObj - The location object.
 * @param {string} props.selectedSemesterName - The currently selected semester (e.g., "1st Semester").
 * @param {Array} props.teacherOverviewData - Teachers payload from parent fetch
 * @param {boolean} props.teachersLoader - Parent teachers fetch loading flag
 * @param {boolean} [props.embedded] - When true, omit outer border (panel body child)
 * @param {string|number} props.schoolYearKey - Selected overview school year key
 * @param {string|number} props.activeSchoolYearKey - Active/current school year key
 * @param {Function} props.setLabelObj - Function to set label object in parent.
 * @param {Function} props.setSelectedLink - Function to set selected link in parent.
 * @returns {JSX.Element}
 */
const LocationTeacherOverview = ({
    locationObj,
    selectedSemesterName,
    teacherOverviewData = [],
    teachersLoader = false,
    embedded = false,
    schoolYearKey,
    activeSchoolYearKey,
    setLabelObj,
    setSelectedLink
}) => {
    const { locationKey, locationName } = locationObj || {};
    const { state: globalState } = useContext(GlobalContext);
    const { token } = globalState || {};
    const navigate = useNavigate();

    const sy = buildOverviewSyParam({ selectedKey: schoolYearKey, activeKey: activeSchoolYearKey });
    // Teacher view and SLE setup always resolve against the active year, so they are dead ends here.
    const isHistoricalYear = sy != null;

    const [filteredLastName, setFilteredLastName] = useState("");

    const navigateToTeacher = ({ teacher }) => {
        const { timePeriodDto } = globalState || {};
        const selectedTimePeriod = timePeriodDto?.find((tp) => tp.name === selectedSemesterName);
        const timePeriodKey = selectedTimePeriod ? selectedTimePeriod.key : "";

        navigate(`/home/teacher-view/${teacher.guid}/${locationKey}/${timePeriodKey}/summary`);
    };

    const handleTeacherCellKeyDown = (event, args) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigateToTeacher(args);
        }
    };

    const [slesLoader, setSlesLoader] = useState(true);
    const [sles, setSles] = useState([]);
    const loader = teachersLoader || slesLoader;

    const handleSetSlesClick = () => {
        if (setSelectedLink) setSelectedLink("HOME/SLES");
        if (setLabelObj) {
            setLabelObj([
                { label: "Home", path: "/home" },
                { label: "SLES", path: "/home/sles" }
            ]);
        }
        navigate("/home/sles");
    };

    useEffect(() => {
        if (!locationKey || !token) {
            setSlesLoader(false);
            return undefined;
        }

        let cancelled = false;
        setSlesLoader(true);

        const fetchSles = async () => {
            try {
                let slesResponse = { data: { payload: [] } };
                if (schoolYearKey) {
                    slesResponse = await ElementaryProgressReportDao({
                        action: "getSchoolLevelExpectations",
                        locationKey,
                        schoolYearKey,
                        token
                    });
                }

                if (cancelled) return;

                if (slesResponse?.data?.payload) {
                    setSles(slesResponse.data.payload);
                } else {
                    setSles([]);
                    toast.error("Something went wrong fetching SLE information for a location.");
                }
            } catch (error) {
                if (cancelled) return;
                console.error(error);
                toast.error("Something went wrong fetching SLE information for a location.");
                setSles([]);
            } finally {
                if (!cancelled) setSlesLoader(false);
            }
        };

        fetchSles();
        return () => {
            cancelled = true;
        };
    }, [locationKey, token, schoolYearKey]);

    // Prepare data helper
    const teacherGroups = useMemo(() => {
        const groups = {};

        // Filter by semester first
        let semesterData = (teacherOverviewData ?? []).filter((teacher) => teacher.timePeriod === selectedSemesterName);
        if (isHistoricalYear) {
            semesterData = filterTeachersWithRosterOrEpr(semesterData);
        }

        // Filter by last name if provided
        if (filteredLastName && filteredLastName.trim() !== "") {
            semesterData = semesterData.filter((teacher) => {
                if (!teacher.employeeName) return false;

                // Extract last name (assuming format is "Last, First")
                const name = teacher.employeeName;
                const lastName = name.includes(",") ? name.split(",")[0].trim() : name.split(" ").pop().trim();

                return lastName.toLowerCase().startsWith(filteredLastName.toLowerCase());
            });
        }

        semesterData.forEach((teacher) => {
            let gradeKey = teacher.teacherGradeName;
            if (gradeKey === "NOT_STARTED") {
                gradeKey = getTeacherGradeNamePrefixFromInstructionalArea(teacher.instructionalArea);
            }

            if (!validTeacherGradeName(gradeKey)) return;

            if (!groups[gradeKey]) {
                groups[gradeKey] = [];
            }
            groups[gradeKey].push(teacher);
        });

        for (const [, teachers] of Object.entries(groups)) {
            teachers.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
        }

        // Sort keys to ensure order: Kindergarten, 1st Grade, 2nd Grade, etc.
        const sortedKeys = sortGrades(Object.keys(groups));

        return sortedKeys.map((key) => ({
            title: getFullGradeName(key),
            teachers: groups[key]
        }));
    }, [teacherOverviewData, selectedSemesterName, filteredLastName, isHistoricalYear]);

    if (!locationKey) {
        return (
            <div className={`${locationTeacherOverviewStyles.roundedBox}`} style={{ color: "white", padding: "20px" }}>
                <h5>No School Selected</h5>
                <p>Please select a school to view the Report Card Overview.</p>
                <button
                    className="action-button-reg"
                    onClick={() => navigate("/home/report_card_overview")}
                    type="button"
                >
                    Back to School List
                </button>
            </div>
        );
    }

    if (loader) {
        return <LoadingSvg />;
    }

    return (
        <div
            className={`${locationTeacherOverviewStyles.roundedBox}${
                embedded ? ` ${locationTeacherOverviewStyles.embedded}` : ""
            }`}
        >
            <div className={locationTeacherOverviewStyles.headerRow}>
                {!isHistoricalYear && (
                    <h4 className={`page-title ${locationTeacherOverviewStyles.schoolTitle}`}>{locationName}</h4>
                )}
                <label className={locationTeacherOverviewStyles.lastNameFilterLabel}>
                    Filter Teachers:{" "}
                    <span className={locationTeacherOverviewStyles.inputWrapper}>
                        <input
                            className={locationTeacherOverviewStyles.lastNameFilter}
                            type="text"
                            placeholder="Filter by Last Name"
                            value={filteredLastName}
                            onChange={(e) => setFilteredLastName(e.target.value)}
                        />
                        {filteredLastName && (
                            <button
                                className={locationTeacherOverviewStyles.clearButton}
                                onClick={() => setFilteredLastName("")}
                                type="button"
                                aria-label="Clear filter"
                            >
                                <Icon iconName="CLOSE" width={14} height={14} fill="#636161" />
                            </button>
                        )}
                    </span>
                </label>
            </div>
            {teacherGroups.length > 0 ? (
                <div className="teacher-overview-groups">
                    {teacherGroups.map((group) => (
                        <div key={group.title} className={locationTeacherOverviewStyles.teacherGroup}>
                            <h5 style={{ marginBottom: "15px" }}>
                                {group.title} ({group.teachers.length} teachers)
                            </h5>
                            <div className={`student-roster-table ${locationTeacherOverviewStyles.responsiveTable}`}>
                                <table className="table table-striped" style={{ textAlign: "center" }}>
                                    <thead style={{ textAlign: "center" }}>
                                        <tr>
                                            <th style={{ textAlign: "center", whiteSpace: "nowrap" }}>Teacher</th>
                                            {isHistoricalYear && (
                                                <th
                                                    style={{
                                                        width: "12%",
                                                        textAlign: "center",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                >
                                                    Guardians Viewed
                                                </th>
                                            )}
                                            {!isHistoricalYear && (
                                                <th
                                                    style={{
                                                        width: "10%",
                                                        textAlign: "center",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                >
                                                    Report Card Created
                                                </th>
                                            )}
                                            <th
                                                style={{
                                                    width: "13%",
                                                    textAlign: "center",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                Report Card Roster Count
                                            </th>
                                            {!isHistoricalYear && (
                                                <th
                                                    style={{
                                                        width: "10%",
                                                        textAlign: "center",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                >
                                                    Report Card Shares
                                                </th>
                                            )}
                                            <th
                                                style={{
                                                    width: "15%",
                                                    textAlign: "center",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                Content Ratings
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip
                                                            id="content-ratings-tooltip"
                                                            className={locationTeacherOverviewStyles.wideTooltip}
                                                        >
                                                            Count of content ratings entered out of total possible based
                                                            on number of students rostered
                                                        </Tooltip>
                                                    }
                                                >
                                                    <i
                                                        className="bi bi-info-circle-fill"
                                                        style={{ marginLeft: "5px", cursor: "pointer" }}
                                                    ></i>
                                                </OverlayTrigger>
                                            </th>
                                            <th
                                                style={{
                                                    width: "15%",
                                                    textAlign: "center",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                SLE Ratings
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip
                                                            id="content-ratings-tooltip"
                                                            className={locationTeacherOverviewStyles.wideTooltip}
                                                        >
                                                            Count of SLE ratings entered out of total possible based on
                                                            number of students rostered
                                                        </Tooltip>
                                                    }
                                                >
                                                    <i
                                                        className="bi bi-info-circle-fill"
                                                        style={{ marginLeft: "5px", cursor: "pointer" }}
                                                    ></i>
                                                </OverlayTrigger>
                                            </th>
                                            <th
                                                style={{
                                                    textAlign: "center",
                                                    width: "12%",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                Comments Added
                                            </th>
                                            <th
                                                style={{
                                                    width: "10%",
                                                    textAlign: "center",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                Report Card Released
                                            </th>
                                            {!isHistoricalYear && (
                                                <th
                                                    style={{
                                                        width: "12%",
                                                        textAlign: "center",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                >
                                                    Guardians Viewed
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody style={{ textAlign: "center" }}>
                                        {group.teachers.map((teacher) => {
                                            const contentComplete =
                                                teacher.overviewCountsDto?.overviewRatingsCountDto
                                                    ?.contentRatingsComplete ?? 0;
                                            const contentTotal =
                                                teacher.overviewCountsDto?.overviewRatingsCountDto
                                                    ?.contentRatingsTotal ?? 0;
                                            const sleComplete =
                                                teacher.overviewCountsDto?.overviewRatingsCountDto
                                                    ?.sleRatingsComplete ?? 0;
                                            const sleTotal =
                                                teacher.overviewCountsDto?.overviewRatingsCountDto?.sleRatingsTotal ??
                                                0;
                                            let sleRatingsCell;
                                            if (sles?.length === 0 && !isHistoricalYear) {
                                                sleRatingsCell = (
                                                    <button
                                                        type="button"
                                                        className={locationTeacherOverviewStyles.setSlesButton}
                                                        onClick={handleSetSlesClick}
                                                    >
                                                        Set SLEs
                                                    </button>
                                                );
                                            } else if (sleComplete === 0 && sleTotal === 0) {
                                                sleRatingsCell = "Not Available";
                                            } else {
                                                sleRatingsCell = `${sleComplete} / ${sleTotal}`;
                                            }

                                            const teacherName = (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center"
                                                    }}
                                                >
                                                    {teacher.employeeStatus !== "ACTIVE" && (
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip id="status-tooltip">User is inactive</Tooltip>
                                                            }
                                                        >
                                                            <span style={{ marginRight: "8px" }}>
                                                                <Icon
                                                                    iconName="ERROR_WARNING"
                                                                    fill="#FEBF31"
                                                                    height={18}
                                                                    width={18}
                                                                />
                                                            </span>
                                                        </OverlayTrigger>
                                                    )}
                                                    <span>{teacher.employeeName}</span>
                                                </div>
                                            );

                                            const guardiansViewedCell = (
                                                <td style={{ textAlign: "center" }} data-label="Guardians Viewed">
                                                    {formatPercentageViewed(teacher.percentageViewed)}
                                                </td>
                                            );

                                            return (
                                                <tr key={teacher.guid}>
                                                    <td
                                                        className={
                                                            isHistoricalYear
                                                                ? locationTeacherOverviewStyles.teacherNameCell
                                                                : locationTeacherOverviewStyles.teacherLinkCell
                                                        }
                                                        data-label="Teacher"
                                                    >
                                                        {isHistoricalYear ? (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip id={`historical-teacher-${teacher.guid}`}>
                                                                        Teacher view is only available for the current
                                                                        school year
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                {teacherName}
                                                            </OverlayTrigger>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className={
                                                                    locationTeacherOverviewStyles.teacherLinkButton
                                                                }
                                                                onClick={() =>
                                                                    navigateToTeacher({
                                                                        navigate,
                                                                        globalState,
                                                                        teacher,
                                                                        selectedSemesterName,
                                                                        locationKey
                                                                    })
                                                                }
                                                                onKeyDown={(event) =>
                                                                    handleTeacherCellKeyDown(event, {
                                                                        navigate,
                                                                        globalState,
                                                                        teacher,
                                                                        selectedSemesterName,
                                                                        locationKey
                                                                    })
                                                                }
                                                            >
                                                                {teacherName}
                                                            </button>
                                                        )}
                                                    </td>
                                                    {isHistoricalYear && guardiansViewedCell}
                                                    {!isHistoricalYear && (
                                                        <td
                                                            style={{ textAlign: "center" }}
                                                            data-label="Report Card Created"
                                                        >
                                                            <Icon
                                                                height={15}
                                                                iconName={
                                                                    teacher.gradingProfileId > 0
                                                                        ? "CHECK_CIRCLE"
                                                                        : "X_CIRCLE"
                                                                }
                                                                fill={
                                                                    teacher.gradingProfileId > 0 ? "#698B26" : "#CB321A"
                                                                }
                                                                width={15}
                                                            />
                                                        </td>
                                                    )}
                                                    <td
                                                        style={{ textAlign: "center" }}
                                                        data-label="Report Card Roster Count"
                                                    >
                                                        {teacher.studentCount ?? 0}
                                                    </td>
                                                    {!isHistoricalYear && (
                                                        <td
                                                            style={{ textAlign: "center" }}
                                                            data-label="Report Card Shares"
                                                        >
                                                            {teacher.shareCount}
                                                        </td>
                                                    )}
                                                    <td style={{ textAlign: "center" }} data-label="Content Ratings">
                                                        {contentComplete === 0 && contentTotal === 0
                                                            ? "Not Available"
                                                            : `${contentComplete} / ${contentTotal}`}
                                                    </td>
                                                    <td style={{ textAlign: "center" }} data-label="SLE Ratings">
                                                        {sleRatingsCell}
                                                    </td>
                                                    <td style={{ textAlign: "center" }} data-label="Comments Added">
                                                        {teacher.overviewCountsDto?.commentsCount ?? 0}
                                                    </td>
                                                    <td
                                                        style={{ textAlign: "center" }}
                                                        data-label="Report Card Released"
                                                    >
                                                        <Icon
                                                            height={15}
                                                            iconName={
                                                                teacher.releaseStatus === "READY_FOR_RELEASE"
                                                                    ? "CHECK_CIRCLE"
                                                                    : "X_CIRCLE"
                                                            }
                                                            fill={
                                                                teacher.releaseStatus === "READY_FOR_RELEASE"
                                                                    ? "#698B26"
                                                                    : "#CB321A"
                                                            }
                                                            width={15}
                                                        />
                                                    </td>
                                                    {!isHistoricalYear && guardiansViewedCell}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p>No teacher overview data available for this selection.</p>
            )}
        </div>
    );
};

LocationTeacherOverview.propTypes = {
    locationObj: PropTypes.shape({
        locationName: PropTypes.string,
        locationKey: PropTypes.string
    }).isRequired,
    selectedSemesterName: PropTypes.string.isRequired,
    teacherOverviewData: PropTypes.arrayOf(PropTypes.object),
    teachersLoader: PropTypes.bool,
    embedded: PropTypes.bool,
    schoolYearKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    activeSchoolYearKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setLabelObj: PropTypes.func,
    setSelectedLink: PropTypes.func
};

export default LocationTeacherOverview;
